/**
 * Tipos transacionais (o núcleo por unidade/ciclo) e a máquina de estados da
 * avaliação (doc 08 §8.7). Valores calculados (score, risco, IMGQ, selo) são
 * derivados — nunca entrada (doc 10.1) — e ficam como cache no backend.
 */

import type { Momento, NivelMaturidade } from "@caint/engine";

/**
 * Papéis do sistema (doc 08 §8.7; ROLES do seed + presidente + admin).
 * `unidade`: conta do estabelecimento avaliado — NÃO participa da avaliação;
 * acessa só o Portal do Plano de Ação (responde os itens FACR da sua unidade).
 */
export type PapelKey =
  | "gqa"
  | "ggo"
  | "ncig"
  | "audin"
  | "coord"
  | "presidente"
  | "admin"
  | "visualizador"
  | "unidade";

/**
 * Cargo de uma conta `unidade` no Portal do Plano de Ação. É apenas
 * **identidade/rótulo** (para a auditoria de "quem subiu a evidência") — NÃO muda
 * a capacidade nem recorta por setor: todos os cargos veem a unidade inteira e a
 * única ação da unidade é anexar/remover evidência.
 */
export type CargoUnidade = "diretor" | "coordenador" | "gerente" | "supervisor";

/** Estados do documento de avaliação (doc 08 §8.7). */
export type EstadoAvaliacao =
  | "RASCUNHO_PRE_VISITA"
  | "EM_CAMPO_INICIAL"
  | "FECHAMENTO_INTERNO"
  | "AGUARDANDO_VALIDACAO"
  | "EM_MONITORAMENTO"
  | "EM_CAMPO_FINAL"
  | "VALIDADO"
  | "ENCAMINHADO";

/** Transições permitidas — a chave avança para os estados listados. */
export const TRANSICOES: Readonly<Record<EstadoAvaliacao, readonly EstadoAvaliacao[]>> = Object.freeze({
  RASCUNHO_PRE_VISITA: ["EM_CAMPO_INICIAL"],
  EM_CAMPO_INICIAL: ["FECHAMENTO_INTERNO"],
  FECHAMENTO_INTERNO: ["AGUARDANDO_VALIDACAO"],
  AGUARDANDO_VALIDACAO: ["EM_MONITORAMENTO"],
  EM_MONITORAMENTO: ["EM_CAMPO_FINAL"],
  EM_CAMPO_FINAL: ["VALIDADO"],
  VALIDADO: ["ENCAMINHADO"],
  ENCAMINHADO: [],
});

const ESTADOS_EDICAO_ITEM: ReadonlySet<EstadoAvaliacao> = new Set<EstadoAvaliacao>([
  "RASCUNHO_PRE_VISITA",
  "EM_CAMPO_INICIAL",
  "EM_CAMPO_FINAL",
]);

/** Avaliadores editam itens só em RASCUNHO_* e EM_CAMPO_* (doc 08 §8.7). */
export function permiteEdicaoDeItem(estado: EstadoAvaliacao): boolean {
  return ESTADOS_EDICAO_ITEM.has(estado);
}

/**
 * Tipo de constatação que lastreia a nota (técnica de auditoria, ISO 19011):
 * evidência não é só foto/arquivo — uma AUSÊNCIA é constatada e registrada, não
 * fotografada. O tipo dirige a obrigatoriedade (anexo nunca é obrigatório; em
 * `declaracao`/`ausencia` pede-se a fonte para permitir a re-performance AUDIN).
 */
export type TipoConstatacao = "documento" | "observacao" | "declaracao" | "ausencia";

/** Resposta de um item, com entradas dos dois momentos e trilha de autoria. */
export interface RespostaItem {
  readonly itemId: string;
  readonly mi?: NivelMaturidade | null;
  readonly mf?: NivelMaturidade | null;
  readonly p?: number;
  readonly i?: number;
  readonly evidenciaMi?: string;
  readonly evidenciaMf?: string;
  readonly tipoMi?: TipoConstatacao | null;
  readonly tipoMf?: TipoConstatacao | null;
  readonly fonteMi?: string;
  readonly fonteMf?: string;
  readonly autor?: string;
  readonly ts?: string; // ISO-8601
}

export interface CachesImgq {
  readonly imgo: number | null;
  readonly imqa: number | null;
  readonly imgr: number | null;
  readonly imgqBruto: number;
  readonly imgqEfetivo: number;
  readonly imgqAjustado: number;
  readonly selo: string;
}

/** Avaliação (transacional). Caches calculados são derivados, recalculáveis. */
export interface Avaliacao {
  readonly id: string;
  readonly unidadeId: string;
  readonly cicloId: string;
  readonly versaoInstrumento: string;
  readonly estado: EstadoAvaliacao;
  readonly respostas: Readonly<Record<string, RespostaItem>>;
  readonly qaciFinal?: number;
  readonly ims?: number;
  readonly cachesInicial?: CachesImgq;
  readonly cachesFinal?: CachesImgq;
  readonly atualizadoEm: string; // ISO-8601, para LWW no sync
}

/* ---------------- Plano de Ação (metodologia FACR) ---------------- */

/** Ciclo de vida do plano de uma avaliação. */
export type EstadoPlano = "aberto" | "em_resposta" | "concluido";

/**
 * Status de execução de um item-ação. Fluxo: `pendente` → (unidade anexa
 * evidência) `em_andamento` → (time CAINT revisa) `concluido` | `rejeitado`;
 * `rejeitado` → (unidade corrige a evidência) `em_andamento`. `rejeitado` só
 * aparece após uma rejeição.
 */
export type StatusPlanoItem = "pendente" | "em_andamento" | "rejeitado" | "concluido";

/** De onde veio o item-ação: gerado pela maturidade 0–2 ou adicionado à mão. */
export type OrigemPlanoItem = "auto" | "manual";

/**
 * Item-ação de um plano, na metodologia FACR: **F**ato (o achado), **A**ção,
 * **C**ausa (raiz) e **R**esponsável. O time interno preenche o plano
 * (Fato/Ação/Causa/Responsável/prazo) e revisa (aprova/rejeita); a **unidade**
 * responde anexando evidência (que move o item para `em_andamento`). Itens CORE
 * (peso 3) são obrigatórios.
 */
/** Uma ação do plano: o que fazer, quem faz e até quando (FACR — A e R). */
export interface PlanoAcaoItem {
  readonly texto: string;
  readonly responsavel: string;
  /** ISO-8601; `null` enquanto a unidade não definiu. */
  readonly prazo: string | null;
}

export interface PlanoItem {
  readonly id: string;
  readonly avaliacaoId: string;
  readonly itemId: string;
  readonly origem: OrigemPlanoItem;
  readonly peso: number | null;
  readonly obrigatorio: boolean;
  /** `mi` (0/1/2) que disparou a geração automática. */
  readonly maturidadeGatilho: string | null;
  /** O gatilho deixou de valer (mi saiu de 0–2) mas o item foi preservado. */
  readonly gatilhoResolvido: boolean;
  readonly fato: string | null;
  /**
   * O achado como está HOJE no checklist. Quando difere de `fato`, alguém
   * reescreveu o texto no plano e o avaliador mudou a evidência depois — a UI
   * mostra a divergência e oferece adotar este texto. `null` em item manual ou
   * anterior a este campo.
   */
  readonly fatoOrigem: string | null;
  /**
   * Texto que uma pessoa havia escrito e que a avaliação substituiu por ser
   * mais antigo. Só fica preenchido até alguém editar o fato de novo — é o que
   * permite avisar quem escreveu, e restaurar. `null` no caso comum.
   */
  readonly fatoSubstituido: string | null;
  /**
   * Ações do item — FONTE ÚNICA do "o que fazer / quem / até quando" (FACR).
   *
   * Cada ação carrega o próprio dono e prazo: cobrança fina, decisão de
   * 24/ago/2026. Por isso `responsavel` e `prazo` do ITEM passaram a ser
   * DERIVADOS (ver abaixo) — dois níveis editáveis para o mesmo fato criariam
   * duas fontes de verdade, que é a classe de bug que já custou caro aqui.
   */
  readonly acoes: readonly PlanoAcaoItem[];
  /** Texto legado da ação única, antes de `acoes`. Só leitura, para migração. */
  readonly acao: string | null;
  readonly causa: string | null;
  /** DERIVADO das ações: o dono da 1ª, ou "N responsáveis". Não editar direto. */
  readonly responsavel: string | null;
  /** DERIVADO das ações: o prazo MAIS PRÓXIMO entre elas. Não editar direto. */
  readonly prazo: string | null; // ISO-8601
  readonly status: StatusPlanoItem;
  /** Motivo da rejeição pelo time CAINT (preenchido só quando status = "rejeitado"). */
  readonly motivoRejeicao: string | null;
  readonly respondidoPor: string | null;
  readonly atualizadoEm: string; // ISO-8601
}

/** Plano de ação de uma avaliação (cabeçalho + itens FACR). */
export interface PlanoAcao {
  readonly avaliacaoId: string;
  readonly unidadeId: string;
  readonly cicloId: string;
  readonly estado: EstadoPlano;
  readonly itens: readonly PlanoItem[];
  readonly criadoEm: string; // ISO-8601
  readonly atualizadoEm: string; // ISO-8601
}

export type { Momento };

/* ---------------- Ações do plano: derivações (FACR) ---------------- */

/** Uma ação está completa quando dá para cobrar: o quê, quem e até quando. */
export function acaoCompleta(a: PlanoAcaoItem): boolean {
  return a.texto.trim() !== "" && a.responsavel.trim() !== "" && !!a.prazo;
}

/**
 * Prazo do ITEM = o mais PRÓXIMO entre as ações. É o que interessa para cobrança
 * e para o relatório: o item vence quando a primeira ação vence.
 */
export function prazoDerivado(acoes: readonly PlanoAcaoItem[]): string | null {
  const prazos = acoes.map((a) => a.prazo).filter((p): p is string => !!p);
  return prazos.length === 0 ? null : prazos.reduce((a, b) => (a < b ? a : b));
}

/**
 * Responsável do ITEM = o dono único, ou "N responsáveis" quando há vários.
 * Serve para a lista e o relatório mostrarem algo sem inventar um dono só.
 */
export function responsavelDerivado(acoes: readonly PlanoAcaoItem[]): string | null {
  const donos = [...new Set(acoes.map((a) => a.responsavel.trim()).filter(Boolean))];
  if (donos.length === 0) {
    return null;
  }
  return donos.length === 1 ? (donos[0] ?? null) : `${donos.length} responsáveis`;
}

/* ---------------- Ordenação do plano por SEVERIDADE ---------------- */

/** O mínimo para ordenar um item — serve `PlanoItem` (DTO) e a linha do banco. */
export interface ItemOrdenavel {
  readonly itemId: string;
  readonly maturidadeGatilho: string | null;
  readonly obrigatorio: boolean;
  readonly peso: number | null;
}

/**
 * Maturidade do gatilho como número, ou `null` (item manual).
 *
 * Existe para não cair na armadilha do `Number(null) === 0`: sem esta checagem,
 * item manual virava "maturidade 0" — a PIOR — e subia para o topo do plano.
 */
function maturidadeDe(item: ItemOrdenavel): number | null {
  if (item.maturidadeGatilho == null || item.maturidadeGatilho === "") {
    return null;
  }
  const m = Number(item.maturidadeGatilho);
  return Number.isFinite(m) ? m : null;
}

/**
 * Gravidade de um item-ação, para ordenar por SEVERIDADE em vez de alfabeto.
 *
 * Deriva só de `maturidadeGatilho` e `obrigatorio` — os dois IMUTÁVEIS depois da
 * geração. É isso que mantém a ordem estável enquanto a unidade preenche: a
 * posição de um item (e do setor dele) não muda porque alguém respondeu.
 *
 * Maturidade menor = buraco maior: 0 (inexistente) pesa mais que 2 (parcial).
 * CORE dobra, porque é o que a metodologia trata como inegociável.
 */
export function gravidadeItem(item: ItemOrdenavel): number {
  const m = maturidadeDe(item);
  if (m === null) {
    return 0; // item manual, sem gatilho de maturidade
  }
  return (3 - m) * (item.obrigatorio ? 2 : 1);
}

/**
 * Ordem dentro do setor: pior maturidade primeiro, CORE desempata, depois peso,
 * e o código do item como último critério — para a ordem ser TOTAL e não
 * depender da estabilidade do `sort`.
 *
 * Mora em `shared` (e não na tela) porque a MESMA ordem vale no Portal da
 * Unidade, no relatório institucional impresso e no gerador do PDF. Três
 * implementações da mesma regra seriam três chances de divergir.
 */
export function compararItensDoPlano(a: ItemOrdenavel, b: ItemOrdenavel): number {
  const va = maturidadeDe(a) ?? 99; // manual vai para o fim
  const vb = maturidadeDe(b) ?? 99;
  if (va !== vb) {
    return va - vb;
  }
  if (a.obrigatorio !== b.obrigatorio) {
    return a.obrigatorio ? -1 : 1;
  }
  if ((a.peso ?? 0) !== (b.peso ?? 0)) {
    return (b.peso ?? 0) - (a.peso ?? 0);
  }
  return a.itemId.localeCompare(b.itemId);
}
